import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router'
import type { ExportView, UserView } from '@olmt/shared'
import {
  exportChat,
  leaveChat,
  loadChatDetails,
  pinChat,
  removeChatMember,
  renameChat,
  setChatDisappearing,
  setChatPhoto,
  muteChat,
} from '../lib/data'
import {
  DISAPPEARING_OPTIONS,
  chatAvatarUrl,
  chatTitle,
  disappearingWindow,
  exportSummary,
  otherMembers,
} from '../lib/chat'
import { formatPresence } from '../lib/time'
import { API_BASE_URL } from '../lib/env'
import { useT } from '../lib/i18n'
import { useAuthStore } from '../stores/authStore'
import { useChatStore, type Presence } from '../stores/chatStore'
import { AddMembersPicker } from '../components/AddMembersPicker'
import { Avatar } from '../components/Avatar'
import { IconButton } from '../components/IconButton'
import { Spinner } from '../components/Spinner'
import {
  ArrowLeftIcon,
  BellIcon,
  BellOffIcon,
  DownloadIcon,
  PinIcon,
  PinOffIcon,
  PlusIcon,
  TimerIcon,
  TrashIcon,
} from '../components/Icons'

/**
 * The chat's details screen (docs/CONTRACT.md §4 `GET|PATCH /api/chats/:id`, `…/members`). Two
 * shapes share one screen: a group is editable — rename, change the photo, add people, remove
 * someone, leave — and a DM is the peer's profile, with none of those destructive controls.
 *
 * Every destructive control is two-step, per the console's delete (apps/admin UsersPage): the
 * first press *arms* an inline confirm next to the row, and only its filled `danger` button acts.
 * `danger` stays the microphone's colour otherwise (docs/DESIGN.md §12.2).
 */
export function ChatDetailsPane() {
  const { chatId = '' } = useParams<{ chatId: string }>()
  const navigate = useNavigate()
  const t = useT()
  const meId = useAuthStore((state) => state.user?.id ?? null)
  const chat = useChatStore((state) => state.chats[chatId])
  const presence = useChatStore((state) => state.presence)

  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<
    'title' | 'photo' | 'member' | 'leave' | 'mute' | 'pin' | 'disappearing' | 'export' | null
  >(null)
  const [adding, setAdding] = useState(false)
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')
  const [armedRemoveId, setArmedRemoveId] = useState<string | null>(null)
  const [armedLeave, setArmedLeave] = useState(false)
  const [exported, setExported] = useState<ExportView | null>(null)
  const photoInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!chatId) return
    let cancelled = false
    // Refresh on every entry: the member list and `memberCount` are what this screen edits, and a
    // deep link (`/chat/:id/details` with no store entry) would otherwise never resolve a chat.
    loadChatDetails(chatId).catch(() => {
      if (!cancelled) setError(t('chat.couldNotLoadChat'))
    })
    return () => {
      cancelled = true
    }
  }, [chatId])

  // Resetting the per-chat edit state when the route changes keeps an armed confirm from following
  // the user to another chat.
  useEffect(() => {
    setEditingTitle(false)
    setArmedRemoveId(null)
    setArmedLeave(false)
    setExported(null)
    setError(null)
  }, [chatId])

  if (!chat) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <PaneHeader title={t('chat.chatDetails')} onBack={() => void navigate('/')} />
        <div className="flex flex-1 items-center justify-center px-6 text-center">
          {error ? (
            <p role="alert" className="text-ui text-danger">
              {error}
            </p>
          ) : (
            <Spinner label={t('chat.loadingChatDetails')} />
          )}
        </div>
      </div>
    )
  }

  const isGroup = chat.kind === 'group'
  const title = chatTitle(chat, meId)
  // The live socket's presence wins over the server's snapshot on the chat view (the same rule
  // `onlineMemberCount` uses), so a row's lamp and its "last seen …" agree with the thread header.
  const presenceFor = (member: UserView): Presence => ({
    online: presence[member.id]?.online ?? member.online,
    lastSeenAt: presence[member.id]?.lastSeenAt ?? member.lastSeenAt,
  })
  const onlineCount = otherMembers(chat, meId).filter((member) => presenceFor(member).online).length

  const saveTitle = async (event: FormEvent) => {
    event.preventDefault()
    const next = titleDraft.trim()
    setBusy('title')
    setError(null)
    try {
      await renameChat(chat.id, next)
      setEditingTitle(false)
    } catch {
      setError(t('chat.couldNotRename'))
    } finally {
      setBusy(null)
    }
  }

  const pickPhoto = async (file: File | undefined) => {
    if (!file) return
    setBusy('photo')
    setError(null)
    try {
      await setChatPhoto(chat.id, file)
    } catch {
      setError(t('chat.couldNotChangePhoto'))
    } finally {
      setBusy(null)
      // Clear the input so picking the same file twice still fires `change`.
      if (photoInput.current) photoInput.current.value = ''
    }
  }

  const removeMember = async (userId: string) => {
    setBusy('member')
    setError(null)
    try {
      await removeChatMember(chat.id, userId)
      setArmedRemoveId(null)
    } catch {
      setError(t('chat.couldNotRemoveMember'))
    } finally {
      setBusy(null)
    }
  }

  const confirmLeave = async () => {
    setBusy('leave')
    setError(null)
    try {
      await leaveChat(chat.id)
      void navigate('/', { replace: true })
    } catch {
      setError(t('chat.couldNotLeave'))
      setBusy(null)
    }
  }

  const setMute = async (until: number | null) => {
    setBusy('mute')
    setError(null)
    try {
      await muteChat(chat.id, until)
    } catch {
      setError(t('chat.couldNotUpdateMute'))
    } finally {
      setBusy(null)
    }
  }

  /**
   * W5 — the viewer's own pin. `GET /api/chats` sorts a member's pinned chats above newer
   * activity, and the returned `ChatView` carries the stamp, so the chat list re-sorts itself the
   * moment the store moves; the row's label flips with `pinnedAt`.
   */
  const setPin = async (pinned: boolean) => {
    setBusy('pin')
    setError(null)
    try {
      await pinChat(chat.id, pinned)
    } catch {
      setError(t('chat.couldNotUpdatePin'))
    } finally {
      setBusy(null)
    }
  }

  /**
   * W13 — the chat's disappearing timer, in the mute row's shape: the press asks, the server's
   * `ChatView` is what moves the row (and `chat.disappearing` carries the same value to everyone
   * else). Any member may set it, `null` turns it off, and the api takes only whole seconds between
   * an hour and a year — the four chips below are windows it accepts.
   */
  const setDisappearing = async (seconds: number | null) => {
    setBusy('disappearing')
    setError(null)
    try {
      await setChatDisappearing(chat.id, seconds)
    } catch {
      setError(t('chat.disappearingFailed'))
    } finally {
      setBusy(null)
    }
  }

  /**
   * W14 — build this member's copy and hand it to the reader. The returned url is a signed link, so
   * the hand-off is a plain anchor click with no bearer token — the same move `downloadMedia` makes
   * for a file message — and the api's `Content-Disposition` is what names the file. Every press
   * builds a fresh archive (the api prunes them after a week), so nothing is cached here; and an
   * export is a copy, so no store moves. The ready line reports the counts and size that went.
   */
  const runExport = async () => {
    setBusy('export')
    setError(null)
    setExported(null)
    try {
      const archive = await exportChat(chat.id)
      const anchor = document.createElement('a')
      // The signed link is a *path* on the **api's** origin, and this app runs on another one: an
      // href without the base would fetch the web bundle's own index.html (which is exactly what
      // happened the first time). Media views resolve their URLs the same way.
      anchor.href = `${API_BASE_URL}${archive.url}`
      // `download` is what keeps the router out of this: React Router intercepts a plain
      // same-origin anchor click and turns it into a client-side navigation — which landed on the
      // app's 404 instead of downloading the archive. With the attribute, the browser downloads,
      // and the api's `Content-Disposition` names the file.
      anchor.download = ''
      anchor.rel = 'noopener'
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      setExported(archive)
    } catch {
      setError(t('chat.exportFailed'))
    } finally {
      setBusy(null)
    }
  }

  const now = Date.now()
  const isMuted = chat.mutedUntil !== null && (chat.mutedUntil === -1 || chat.mutedUntil > now)
  const isPinned = chat.pinnedAt !== null
  const muteLabel = !isMuted
    ? t('chat.notificationsOn')
    : chat.mutedUntil === -1
      ? t('chat.mutedForever')
      : t('chat.mutedUntil', {
          date: new Date(chat.mutedUntil ?? 0).toLocaleDateString([], { month: 'short', day: 'numeric' }),
        })

  const peer = isGroup ? null : otherMembers(chat, meId)[0] ?? null
  const peerPresence = peer ? presenceFor(peer) : null

  const subtitle = isGroup
    ? t('chat.groupSubtitle', {
        members: t(chat.memberCount === 1 ? 'chat.memberCountOne' : 'chat.memberCountOther', {
          count: chat.memberCount,
        }),
        online: onlineCount,
      })
    : peerPresence
      ? formatPresence(peerPresence.online, peerPresence.lastSeenAt)
      : t('chat.offline')

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PaneHeader
        title={isGroup ? t('chat.groupDetails') : t('chat.contactInfo')}
        onBack={() => void navigate(`/chat/${chatId}`)}
      />

      {error && (
        <p role="alert" className="border-b border-line bg-surface px-4 py-1.5 text-meta text-danger">
          {error}
        </p>
      )}

      <div className="chat-scroll">
        <div className="room-column flex flex-col gap-5 px-3 py-5">
          <section className="flex flex-col items-center gap-3 text-center">
            <Avatar
              name={title}
              path={chatAvatarUrl(chat, meId)}
              size={96}
              online={isGroup ? onlineCount > 0 : (peerPresence?.online ?? false)}
            />

            {isGroup && editingTitle ? (
              <form className="flex w-full max-w-sm flex-col gap-2" onSubmit={(event) => void saveTitle(event)}>
                <label htmlFor="chat-title" className="text-start text-meta text-muted">
                  {t('chat.groupName')}
                </label>
                <input
                  id="chat-title"
                  value={titleDraft}
                  onChange={(event) => setTitleDraft(event.target.value)}
                  maxLength={80}
                  autoFocus
                  className="min-h-11 rounded-well bg-surface2 px-3 text-body text-ink ring-1 ring-line ring-inset"
                />
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={busy === 'title'}
                    className="min-h-11 flex-1 rounded-full bg-accent text-ui font-semibold press text-on-accent hover:bg-accent-strong disabled:opacity-40"
                  >
                    {t('chat.save')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingTitle(false)}
                    className="min-h-11 flex-1 rounded-full text-ui font-medium text-ink ring-1 ring-line ring-inset transition-colors duration-quick hover:bg-surface2"
                  >
                    {t('chat.cancel')}
                  </button>
                </div>
              </form>
            ) : (
              <>
                <h2 className="text-display font-semibold text-ink">
                  <bdi>{title}</bdi>
                </h2>
                {peer && <p className="text-ui text-muted">@{peer.handle}</p>}
                <p role="status" className="text-ui text-muted">
                  {subtitle}
                </p>
              </>
            )}

            {isGroup && !editingTitle && (
              <div className="flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setTitleDraft(chat.title ?? '')
                    setEditingTitle(true)
                  }}
                  className="min-h-11 rounded-full px-4 text-ui font-medium text-ink ring-1 ring-line ring-inset transition-colors duration-quick hover:bg-surface2"
                >
                  {t('chat.rename')}
                </button>
                <button
                  type="button"
                  disabled={busy === 'photo'}
                  onClick={() => photoInput.current?.click()}
                  className="press min-h-11 rounded-full px-4 text-ui font-medium text-ink ring-1 ring-line ring-inset transition-colors duration-quick hover:bg-surface2 disabled:opacity-40"
                >
                  {busy === 'photo' ? t('chat.photoUploading') : t('chat.changePhoto')}
                </button>
              </div>
            )}
            <input
              ref={photoInput}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(event) => void pickPhoto(event.target.files?.[0])}
            />
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="px-1 text-meta font-semibold tracking-wide text-muted uppercase">
              {t('chat.notifications')}
            </h3>
            <div className="overflow-hidden rounded-panel bg-surface p-3 ring-1 ring-line ring-inset">
              <div className="flex items-center gap-3">
                <span
                  aria-hidden="true"
                  className={`flex size-10 shrink-0 items-center justify-center rounded-full ${
                    isMuted ? 'bg-surface2 text-muted' : 'bg-accent/15 text-accent'
                  }`}
                >
                  {isMuted ? <BellOffIcon size={20} /> : <BellIcon size={20} />}
                </span>
                <div className="flex flex-1 flex-col">
                  <span className="text-ui font-medium text-ink">{isMuted ? t('chat.muted') : t('chat.unmuted')}</span>
                  <span className="text-meta text-muted">{muteLabel}</span>
                </div>
                {busy === 'mute' && <Spinner size={16} label={t('chat.updatingMute')} />}
              </div>
              <div className="mt-3 flex flex-wrap gap-2 pt-2 border-t border-line">
                <button
                  type="button"
                  disabled={busy === 'mute'}
                  onClick={() => void setMute(Date.now() + 8 * 3600 * 1000)}
                  className="press min-h-10 rounded-full px-3 text-meta font-medium text-ink ring-1 ring-line ring-inset hover:bg-surface2 disabled:opacity-40"
                >
                  {t('chat.mute8Hours')}
                </button>
                <button
                  type="button"
                  disabled={busy === 'mute'}
                  onClick={() => void setMute(Date.now() + 7 * 24 * 3600 * 1000)}
                  className="press min-h-10 rounded-full px-3 text-meta font-medium text-ink ring-1 ring-line ring-inset hover:bg-surface2 disabled:opacity-40"
                >
                  {t('chat.mute1Week')}
                </button>
                <button
                  type="button"
                  disabled={busy === 'mute'}
                  onClick={() => void setMute(-1)}
                  className="press min-h-10 rounded-full px-3 text-meta font-medium text-ink ring-1 ring-line ring-inset hover:bg-surface2 disabled:opacity-40"
                >
                  {t('chat.muteForever')}
                </button>
                {isMuted && (
                  <button
                    type="button"
                    disabled={busy === 'mute'}
                    onClick={() => void setMute(null)}
                    className="press min-h-10 rounded-full px-3 text-meta font-medium text-accent ring-1 ring-accent/30 ring-inset hover:bg-accent/10 disabled:opacity-40"
                  >
                    {t('chat.unmute')}
                  </button>
                )}
              </div>
            </div>
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="px-1 text-meta font-semibold tracking-wide text-muted uppercase">
              {t('chat.pin')}
            </h3>
            {/* W5 — the viewer's own pin, in the mute row's shape: a state glyph, the state in
                words, and the one action that flips it. It is not the chat's state and nobody
                else's list moves, so the copy says what it does: the row goes to the top. */}
            <div className="overflow-hidden rounded-panel bg-surface p-3 ring-1 ring-line ring-inset">
              <div className="flex items-center gap-3">
                <span
                  aria-hidden="true"
                  className={`flex size-10 shrink-0 items-center justify-center rounded-full ${
                    isPinned ? 'bg-accent/15 text-accent' : 'bg-surface2 text-muted'
                  }`}
                >
                  {isPinned ? <PinIcon size={20} /> : <PinOffIcon size={20} />}
                </span>
                <div className="flex flex-1 flex-col">
                  <span className="text-ui font-medium text-ink">
                    {isPinned ? t('chat.chatPinned') : t('chat.chatUnpinned')}
                  </span>
                </div>
                {busy === 'pin' && <Spinner size={16} label={t('chat.updatingPin')} />}
              </div>
              <div className="mt-3 flex flex-wrap gap-2 pt-2 border-t border-line">
                <button
                  type="button"
                  disabled={busy === 'pin'}
                  onClick={() => void setPin(!isPinned)}
                  className="press min-h-10 rounded-full px-3 text-meta font-medium text-ink ring-1 ring-line ring-inset hover:bg-surface2 disabled:opacity-40"
                >
                  {isPinned ? t('chat.unpin') : t('chat.pin')}
                </button>
              </div>
            </div>
          </section>

          {/* W13 — the chat's disappearing-message timer, in the mute and pin rows' shape: a glyph,
              the current value in words, and the windows as chips. It is the *chat's* setting, not
              the viewer's, so the hint says who it applies to and that off is where every chat
              starts. The pressed chip is marked by its fill and a `aria-pressed`, never by hue
              alone. */}
          <section className="flex flex-col gap-2">
            <h3 className="px-1 text-meta font-semibold tracking-wide text-muted uppercase">
              {t('chat.disappearingMessages')}
            </h3>
            <div className="overflow-hidden rounded-panel bg-surface p-3 ring-1 ring-line ring-inset">
              <div className="flex items-center gap-3">
                <span
                  aria-hidden="true"
                  className={`flex size-10 shrink-0 items-center justify-center rounded-full ${
                    chat.disappearingSeconds === null ? 'bg-surface2 text-muted' : 'bg-accent/15 text-accent'
                  }`}
                >
                  <TimerIcon size={20} />
                </span>
                <div className="flex flex-1 flex-col">
                  <span className="text-ui font-medium text-ink">
                    {disappearingWindow(chat.disappearingSeconds)}
                  </span>
                </div>
                {busy === 'disappearing' && <Spinner size={16} label={t('chat.disappearingUpdating')} />}
              </div>
              <div className="mt-3 flex flex-wrap gap-2 border-t border-line pt-2">
                {DISAPPEARING_OPTIONS.map((option) => {
                  const active = option === chat.disappearingSeconds
                  return (
                    <button
                      key={option ?? 'off'}
                      type="button"
                      aria-pressed={active}
                      disabled={busy === 'disappearing'}
                      onClick={() => void setDisappearing(option)}
                      className={`press min-h-11 rounded-full px-3 text-meta font-medium ring-1 ring-inset transition-colors duration-quick disabled:opacity-40 ${
                        active
                          ? 'bg-surface3 text-ink ring-line-strong'
                          : 'text-ink ring-line hover:bg-surface2'
                      }`}
                    >
                      {disappearingWindow(option)}
                    </button>
                  )
                })}
              </div>
              <p className="mt-2 text-meta text-muted">{t('chat.disappearingHint')}</p>
            </div>
          </section>

          {/* W14 — the export row, in the mute and pin rows' shape: a glyph, what a press gives the
              reader, and the one action. The hint is the honest part — the archive holds a copy of
              the messages and their media, is built for the reader and pruned after a week, and the
              chat itself is never touched. The spinner is the panel's busy pattern; the ready line
              is `role="status"` because it answers the press with what went. */}
          <section className="flex flex-col gap-2">
            <h3 className="px-1 text-meta font-semibold tracking-wide text-muted uppercase">
              {t('chat.export')}
            </h3>
            <div className="overflow-hidden rounded-panel bg-surface p-3 ring-1 ring-line ring-inset">
              <div className="flex items-center gap-3">
                <span
                  aria-hidden="true"
                  className="flex size-10 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent"
                >
                  <DownloadIcon size={20} />
                </span>
                <div className="flex flex-1 flex-col">
                  <span className="text-ui font-medium text-ink">{t('chat.exportTitle')}</span>
                  <span className="text-meta text-muted">{t('chat.exportHint')}</span>
                </div>
                {busy === 'export' && <Spinner size={16} label={t('chat.exportBuilding')} />}
              </div>
              <div className="mt-3 flex flex-wrap gap-2 border-t border-line pt-2">
                <button
                  type="button"
                  disabled={busy === 'export'}
                  onClick={() => void runExport()}
                  className="press min-h-11 rounded-full px-4 text-ui font-medium text-ink ring-1 ring-line ring-inset transition-colors duration-quick hover:bg-surface2 disabled:opacity-40"
                >
                  {t('chat.export')}
                </button>
              </div>
              {exported && (
                <p role="status" className="mt-2 text-meta text-muted">
                  {exportSummary(exported)}
                </p>
              )}
            </div>
          </section>

          {isGroup && (
            <section className="flex flex-col gap-2">
              <h3 className="px-1 text-meta font-semibold tracking-wide text-muted uppercase">
                {t('chat.membersHeading', { count: chat.memberCount })}
              </h3>
              <ul className="overflow-hidden rounded-panel bg-surface ring-1 ring-line ring-inset">
                {chat.members.map((member) => (
                  <MemberRow
                    key={member.id}
                    member={member}
                    isSelf={member.id === meId}
                    presence={presenceFor(member)}
                    removable={member.id !== meId}
                    armed={armedRemoveId === member.id}
                    busy={busy === 'member'}
                    onArm={() => setArmedRemoveId(member.id)}
                    onCancel={() => setArmedRemoveId(null)}
                    onConfirm={() => void removeMember(member.id)}
                  />
                ))}
                <li className="border-t border-line">
                  <button
                    type="button"
                    onClick={() => setAdding(true)}
                    className="flex min-h-14 w-full items-center gap-3 px-3 text-start transition-colors duration-quick hover:bg-surface2/60"
                  >
                    <span
                      aria-hidden="true"
                      className="flex size-11 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent ring-1 ring-accent/25 ring-inset"
                    >
                      <PlusIcon size={20} />
                    </span>
                    <span className="text-ui font-medium text-accent">{t('chat.addPeople')}</span>
                  </button>
                </li>
              </ul>
            </section>
          )}

          {isGroup && (
            <section>
              {armedLeave ? (
                <div className="flex flex-col gap-3 rounded-panel bg-danger/10 p-3 ring-1 ring-danger/30 ring-inset">
                  <p className="text-ui text-danger">{t('chat.leaveConfirm', { name: title })}</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      disabled={busy === 'leave'}
                      onClick={() => void confirmLeave()}
                      className="press flex min-h-11 items-center gap-2 rounded-full bg-danger px-4 text-ui font-semibold text-on-danger hover:brightness-110 disabled:opacity-50"
                    >
                      {busy === 'leave' && <Spinner size={16} label={t('chat.leaving')} />}
                      {t('chat.leave')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setArmedLeave(false)}
                      className="press min-h-11 rounded-full px-4 text-ui font-medium text-ink ring-1 ring-line ring-inset hover:bg-surface2"
                    >
                      {t('chat.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setArmedLeave(true)}
                  className="min-h-11 w-full rounded-full text-ui font-medium text-danger ring-1 ring-danger/40 ring-inset transition-colors duration-quick hover:bg-danger/10"
                >
                  {t('chat.leaveChat')}
                </button>
              )}
            </section>
          )}
        </div>
      </div>

      {adding && (
        <AddMembersPicker chat={chat} onClose={() => setAdding(false)} onAdded={() => setAdding(false)} />
      )}
    </div>
  )
}

function PaneHeader({ title, onBack }: { title: string; onBack: () => void }) {
  const t = useT()
  return (
    <header className="app-header flex items-center gap-2 border-b border-line bg-surface">
      <IconButton label={t('chat.back')} onClick={onBack}>
        <ArrowLeftIcon size={22} />
      </IconButton>
      <h1 className="min-w-0 flex-1 truncate text-title font-semibold text-ink">{title}</h1>
    </header>
  )
}

type MemberRowProps = {
  member: UserView
  isSelf: boolean
  presence: Presence
  removable: boolean
  armed: boolean
  busy: boolean
  onArm: () => void
  onCancel: () => void
  onConfirm: () => void
}

function MemberRow({ member, isSelf, presence, removable, armed, busy, onArm, onCancel, onConfirm }: MemberRowProps) {
  const t = useT()
  return (
    <li className="border-b border-line last:border-b-0">
      <div className="flex items-center gap-3 px-3 py-2">
        <Avatar name={member.displayName} path={member.avatarUrl} size={44} online={presence.online} />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="min-w-0 truncate text-ui font-medium text-ink">
              <bdi>{member.displayName}</bdi>
            </span>
            {isSelf && (
              <span className="shrink-0 rounded-full bg-surface2 px-2 py-0.5 text-meta font-medium text-muted ring-1 ring-line-strong ring-inset">
                {t('chat.youBadge')}
              </span>
            )}
          </span>
          <span className="block truncate text-meta text-muted">
            @{member.handle} · {formatPresence(presence.online, presence.lastSeenAt)}
          </span>
        </span>
        {removable && (
          <IconButton
            label={t('chat.removeMember', { name: member.displayName })}
            variant="destructive"
            size={40}
            onClick={onArm}
          >
            <TrashIcon size={18} />
          </IconButton>
        )}
      </div>
      {armed && (
        <div className="flex flex-wrap items-center gap-2 px-3 pt-1 pb-3 ps-[68px]">
          <span className="min-w-0 flex-1 text-meta text-danger">
            {t('chat.removeMemberConfirm', { name: member.displayName })}
          </span>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className="press flex min-h-11 items-center gap-2 rounded-full bg-danger px-4 text-ui font-semibold text-on-danger hover:brightness-110 disabled:opacity-50"
          >
            {busy && <Spinner size={16} label={t('chat.removing')} />}
            {t('chat.remove')}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="press min-h-11 rounded-full px-4 text-ui font-medium text-ink ring-1 ring-line ring-inset hover:bg-surface2"
          >
            {t('chat.cancel')}
          </button>
        </div>
      )}
    </li>
  )
}
